import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { qnaService } from '../../../services/qnaService';
import { useAuthStore } from '../../../store/authStore';
import Button from '../../atoms/Button';

const Container = styled.div`
  margin-top: 24px;
`;

const QnAList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QnAItem = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid ${props => props.$isAnswered ? '#28a745' : '#ffc107'};
`;

const QnAHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.$isAnswered ? '#d4edda' : '#fff3cd'};
  color: ${props => props.$isAnswered ? '#155724' : '#856404'};
`;

const ProgramTitle = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

const QuestionSection = styled.div`
  margin-bottom: 16px;
`;

const QuestionLabel = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 600;
  margin-bottom: 8px;
`;

const QuestionText = styled.div`
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AnswerSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
`;

const AnswerLabel = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 600;
  margin-bottom: 8px;
`;

const AnswerText = styled.div`
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const DateText = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 8px;
`;

const AnswerForm = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 2px solid #e9ecef;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;

  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }
`;

const SelectedFilesText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #666;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const QnABlock = ({ studentId }) => {
  const { userProfile } = useAuthStore();
  const [qnaList, setQnaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answeringQnaId, setAnsweringQnaId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answerImages, setAnswerImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (studentId) {
      fetchQnAs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchQnAs = async () => {
    try {
      setLoading(true);
      const data = await qnaService.getStudentQnAs(studentId);
      setQnaList(data);
    } catch (error) {
      console.error('Failed to fetch QnAs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      return dateObj.toLocaleString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const handleAnswerClick = (qnaId) => {
    setAnsweringQnaId(qnaId);
    setAnswerText('');
    setAnswerImages([]);
  };

  const handleCancelAnswer = () => {
    setAnsweringQnaId(null);
    setAnswerText('');
    setAnswerImages([]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAnswerImages(files);
  };

  const handleSubmitAnswer = async (qna) => {
    if (!answerText.trim()) {
      alert('답변 내용을 입력해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      await qnaService.answerQuestion(
        qna.id,
        {
          userId: userProfile.id,
          userName: userProfile.name,
          content: answerText
        },
        answerImages
      );

      alert('답변이 등록되었습니다.');
      setAnsweringQnaId(null);
      setAnswerText('');
      setAnswerImages([]);
      fetchQnAs();
    } catch (error) {
      console.error('Failed to submit answer:', error);
      alert('답변 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const canAnswer = userProfile?.type === 'teacher' || userProfile?.type === 'admin';

  // 답변 메시지 여부 확인
  const hasInstructorReply = (messages) => {
    if (!messages || messages.length === 0) return false;
    return messages.some(msg => msg.userType === 'instructor');
  };

  if (loading) {
    return <LoadingMessage>QnA를 불러오는 중...</LoadingMessage>;
  }

  return (
    <Container>
      {qnaList.length === 0 ? (
        <EmptyMessage>등록된 질문이 없습니다.</EmptyMessage>
      ) : (
        <QnAList>
        {qnaList.map((qna) => {
          const isAnswered = qna.status === 'answered' || hasInstructorReply(qna.messages);
          const firstMessage = qna.messages?.[0];

          return (
            <QnAItem key={qna.id} $isAnswered={isAnswered}>
              <QnAHeader>
                <ProgramTitle>
                  {qna.childName ? `${qna.childName}` : qna.parentName}
                </ProgramTitle>
                <StatusBadge $isAnswered={isAnswered}>
                  {isAnswered ? '답변 완료' : '답변 대기'}
                </StatusBadge>
              </QnAHeader>

              {/* 제목 */}
              <QuestionSection>
                <QuestionLabel>{qna.title}</QuestionLabel>

                {/* 첫 번째 메시지 (질문) */}
                {firstMessage && (
                  <>
                    <QuestionText>{firstMessage.content}</QuestionText>
                    <DateText>
                      {firstMessage.userName} • {formatDate(firstMessage.createdAt)}
                    </DateText>

                    {firstMessage.imageUrls && firstMessage.imageUrls.length > 0 && (
                      <ImageGrid>
                        {firstMessage.imageUrls.map((url, index) => (
                          <ImageWrapper key={index} onClick={() => handleImageClick(url)}>
                            <Image src={url} alt={`질문 이미지 ${index + 1}`} />
                          </ImageWrapper>
                        ))}
                      </ImageGrid>
                    )}
                  </>
                )}
              </QuestionSection>

              {/* 모든 메시지 표시 (두 번째 메시지부터) */}
              {qna.messages && qna.messages.length > 1 && (
                <>
                  {qna.messages.slice(1).map((message, idx) => (
                    <AnswerSection key={message.id || idx}>
                      <AnswerLabel>
                        {message.userType === 'instructor' ? '답변' : '추가 메시지'}
                      </AnswerLabel>
                      <AnswerText>{message.content}</AnswerText>
                      <DateText>
                        {message.userName} • {formatDate(message.createdAt)}
                      </DateText>

                      {message.imageUrls && message.imageUrls.length > 0 && (
                        <ImageGrid>
                          {message.imageUrls.map((url, index) => (
                            <ImageWrapper key={index} onClick={() => handleImageClick(url)}>
                              <Image src={url} alt={`메시지 이미지 ${index + 1}`} />
                            </ImageWrapper>
                          ))}
                        </ImageGrid>
                      )}
                    </AnswerSection>
                  ))}
                </>
              )}

              {/* 답변 폼 (치료사/관리자만) */}
              {canAnswer && (
                <>
                  {answeringQnaId === qna.id ? (
                    <AnswerForm>
                      <AnswerLabel>답변 작성</AnswerLabel>
                      <Textarea
                        value={answerText}
                        onChange={(e) => setAnswerText(e.target.value)}
                        placeholder="답변 내용을 입력하세요..."
                      />
                      <FileInput
                        type="file"
                        id={`answer-file-${qna.id}`}
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <FileLabel htmlFor={`answer-file-${qna.id}`}>
                        이미지 첨부 (선택사항)
                      </FileLabel>
                      {answerImages.length > 0 && (
                        <SelectedFilesText>
                          {answerImages.length}개 파일 선택됨
                        </SelectedFilesText>
                      )}
                      <ButtonGroup>
                        <Button
                          onClick={() => handleSubmitAnswer(qna)}
                          disabled={submitting}
                        >
                          {submitting ? '등록 중...' : '답변 등록'}
                        </Button>
                        <Button
                          onClick={handleCancelAnswer}
                          disabled={submitting}
                          variant="secondary"
                        >
                          취소
                        </Button>
                      </ButtonGroup>
                    </AnswerForm>
                  ) : (
                    <ButtonGroup>
                      <Button onClick={() => handleAnswerClick(qna.id)}>
                        답변하기
                      </Button>
                    </ButtonGroup>
                  )}
                </>
              )}
            </QnAItem>
          );
        })}
      </QnAList>
      )}

      {selectedImage && (
        <ImageModal onClick={handleCloseModal}>
          <ModalImage src={selectedImage} alt="확대 이미지" />
        </ImageModal>
      )}
    </Container>
  );
};

export default QnABlock;
